/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable unused-imports/no-unused-vars */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "../typography/typography";

import Button from "../button/button";
import Avatar from "../avatar/avatar";
import Select from "../select/select";
import "./share-modal.scss";
import { useEffect, useMemo, useState } from "react";
import React from "react";
import Toggle from "../toggle/toggle";
import SearchableSelect from "../searchable-select";
import { flatMap } from "lodash";
import Checkbox from "../checkbox/checkbox";
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { getAllUserAndDepartmentDropdown } from '../../redux/slices';

// Enhanced interface that supports both Redux and prop-based data
interface SharePopUpEnhancedProps {
  open: boolean;
  title: string;
  onClose: () => void;
  roles?: any;
  toggleSwitch: boolean;
  onSubmit: (selectedUsers: any) => void;
  value?: any;
  isPeopleAccessVisible?: boolean;
  
  // New props for non-Redux usage
  users?: any[];
  departments?: any[];
  loading?: boolean;
  error?: string;
  onFetchUsersAndDepartments?: () => Promise<void>;
  
  // Redux integration (optional)
  useRedux?: boolean;
}

interface UserDropdownData {
  isRoleVisible: boolean;
  selectedUsers: any[];
}

const SharePopUpEnhanced = (props: SharePopUpEnhancedProps) => {
  const {
    open,
    title,
    onClose,
    roles,
    toggleSwitch,
    onSubmit,
    value = {},
    isPeopleAccessVisible = false,
    
    // Non-Redux props
    users: propUsers = [],
    departments: propDepartments = [],
    loading: propLoading = false,
    error: propError = "",
    onFetchUsersAndDepartments,
    
    // Redux integration
    useRedux = false,
  } = props;

  // Conditional Redux usage
  let reduxState = { users: [], departments: [], loading: false, error: "", isError: false };
  let dispatch: any = null;
  
  if (useRedux) {
    try {
      // Redux hooks and actions (statically imported)
      
      reduxState = useAppSelector((store: any) => store.share || reduxState);
      dispatch = useAppDispatch();
    } catch (error) {
      console.warn("Redux not available for ShareModal, falling back to prop-based data");
    }
  }

  // Use Redux data if available, otherwise use props
  const users = useRedux ? reduxState.users : propUsers;
  const departments = useRedux ? reduxState.departments : propDepartments;
  const loading = useRedux ? reduxState.loading : propLoading;
  const error = useRedux ? reduxState.error : propError;

  const [isPublic, setIsPublic] = useState(value.isPublic || false);
  const [selectedOptions, setSelectedOptions] = useState(
    value.selectedUsers || [],
  );
  const [roleValue, setRoleValue] = useState([]);
  const [availableRole, setAvailableRole] = useState([]);

  const [userDropdownData, setUserDropdownData] = useState<UserDropdownData>({
    isRoleVisible: false,
    selectedUsers: [],
  });

  const filteredOptions = useMemo(
    () => userDropdownData?.isRoleVisible ? departments : users,
    [users, departments, userDropdownData?.isRoleVisible]
  );
  
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  useEffect(() => {
    const role: any = [];
    const selRole: any = [];
    roles &&
      roles.map((item: any) => {
        const userRole: any = {};
        if (item.role !== "Owner") {
          role.push(item.role);
          userRole.id = item.id;
          userRole.role = item.role;
          selRole.push(userRole);
        }
        setAvailableRole(role);
        setRoleValue(selRole);
      });
  }, [roles]);

  useEffect(() => {
    if (!value?.selectedUsers?.length && !value?.role_id) {
      resetOptions();
      return;
    }
    userDropdownData.isRoleVisible = value.is_role;
    setUserDropdownData(userDropdownData);
    setSelectedOptions({
      ...value,
      selectedUsers: value.is_role ? value?.role_id : value?.selectedUsers
    });
    //eslint-disable-next-line
  }, [value?.selectedUsers, open]);

  const handleShareSubmit = () => {
    if (!selectedUsers?.length) return;
    const uIds = flatMap(selectedUsers, 'userIds').filter(i => i);
    let sOptions = selectedUsers;
    if (Array.isArray(uIds) && uIds?.length) {
      sOptions = users?.filter((u: any) => uIds.includes(u.id)) || [];
    }
    const sharedData = {
      is_private: !isPublic,
      selectedUsers: [...new Set(sOptions)],
      is_role: userDropdownData?.isRoleVisible,
      role_id: userDropdownData?.isRoleVisible ? selectedUsers?.map((u: any) => u?.id) : [],
    };
    onSubmit(sharedData);
  };

  const resetOptions = () => {
    setSelectedOptions({
      ...value
    });
    setUserDropdownData((prevData) => ({
      ...prevData,
      selectedUsers: [],
    }));
  };

  const handleRoleChange = (event: any, id: any, user: any) => {
    const changeRole: any = roleValue.map((item: any) => {
      if (item.id === id) {
        item.role = event.target.value;
      }
      return item;
    });
    setRoleValue(changeRole);
  };

  const handleToggleChange = () => {
    setUserDropdownData((prevSettings) => ({
      ...prevSettings,
      isRoleVisible: !prevSettings.isRoleVisible,
    }));
    setSelectedOptions([]);
    setSelectedUsers(value.selectedUsers || []);
  };

  const handleChage = (value: any) => {
    if (userDropdownData.isRoleVisible) {
      setSelectedOptions({ ...selectedOptions, role_id: value });
    }
    setSelectedOptions({ ...selectedOptions, selectedUsers: value });
  };

  const toggleMakeItPublic = () => {
    setIsPublic(!isPublic);
  };

  useEffect(() => {
    if (!open) resetOptions();
    if (open) {
      if (useRedux && dispatch) {
        // Use Redux action
        dispatch(getAllUserAndDepartmentDropdown());
      } else if (onFetchUsersAndDepartments) {
        // Use prop-based data fetching
        onFetchUsersAndDepartments();
      }
    }
    // eslint-disable-next-line
  }, [open, useRedux, dispatch, onFetchUsersAndDepartments]);

  // Show loading state
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} className="share">
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading users and departments...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Show error state
  if (error) {
    return (
      <Dialog open={open} onClose={onClose} className="share">
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" minHeight="200px">
            <Typography color="error">Error: {error}</Typography>
            <Button 
              onClick={() => {
                if (useRedux && dispatch) {
                  dispatch(getAllUserAndDepartmentDropdown());
                } else if (onFetchUsersAndDepartments) {
                  onFetchUsersAndDepartments();
                }
              }}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div>
      <Dialog open={open} onClose={onClose} className="share">
        <DialogTitle className="share--head">
          <Box className="share--title">
            <Typography type="s3" ml={1} className="dialog-box-title">
              {title}
            </Typography>
          </Box>
          <IconButton disableRipple onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent className="share--content">
          <Box m={1} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography type="s3" className="input-label">
              Add users
            </Typography>
            <FormControlLabel
              control={<Box mb={0.5} mr={0.5}><Toggle checked={userDropdownData?.isRoleVisible} onChange={handleToggleChange} /></Box>}
              label={
                <Typography type="s3" className="input-label">Show only Roles</Typography>
              }
            />
          </Box>

          {toggleSwitch && (
            <>
              <Box mb={4} className="d-flex">
                <SearchableSelect
                  options={filteredOptions}
                  multiple
                  value={selectedOptions?.selectedUsers}
                  onChange={(e) => handleChage(e.target.value)}
                  placeholder={
                    selectedOptions && selectedOptions.length
                      ? ""
                      : "Add people and roles"
                  }
                  getSelectedData={row => setSelectedUsers(row)}
                />
              </Box>
            </>
          )}

          {isPeopleAccessVisible && (
            <div className={"share--blurClass"}>
              <Accordion className="people-access-accordion">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>People with access</Typography>
                </AccordionSummary>
                <AccordionDetails className="user-accordion-details">
                  <List className="accordion-list">
                    {roles &&
                      roles.map((user: any) => {
                        const userRole: any =
                          roleValue?.length > 0 &&
                          roleValue?.filter((item: any) => item.id === user.id);
                        return (
                          <ListItem alignItems="flex-start" key={user.id}>
                            <ListItemAvatar>
                              <Avatar size={40} alt="Person" icon />
                            </ListItemAvatar>
                            <ListItemText
                              className="primary-user-name"
                              primary={user.name}
                              secondary={
                                <Typography
                                  component="span"
                                  variant="body2"
                                  className="secondary-email-text"
                                >
                                  {user.email}
                                </Typography>
                              }
                            />
                            {user.role == "Owner" ? (
                              <p className="owner-role-badge">{user.role}</p>
                            ) : (
                              <div>
                                <Select
                                  className="role-edit-select-box"
                                  value={
                                    userRole?.length > 0 ? userRole[0].role : ""
                                  }
                                  onChange={(e: any) =>
                                    handleRoleChange(e, user.id, user)
                                  }
                                  variant="outlined"
                                >
                                  {availableRole.map((role: any) => (
                                    <MenuItem
                                      className="select-menu-items"
                                      value={role}
                                      key={role}
                                    >
                                      {role}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                            )}
                          </ListItem>
                        );
                      })}
                  </List>
                </AccordionDetails>
              </Accordion>
            </div>
          )}

          <div className="checkbox">
            <Checkbox
              checked={isPublic}
              onChange={toggleMakeItPublic}
            />
            <div className="checkbox-text">Make it public</div>
          </div>
        </DialogContent>

        <Divider />

        <DialogActions className="share--actions">
          <Button className="cancel-btn" variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button className="success-btn" onClick={handleShareSubmit}>
            Save and Share
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export { SharePopUpEnhanced };
export default SharePopUpEnhanced;